import { StoreOpeningEvaluationStatus } from "@src/types.generated";
import { Button } from "@src/lib/ui/Button";
import { DatePicker } from "@src/lib/ui/DatePicker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import {
  HoverCard,
  HoverCardButton,
  HoverCardContent,
  HoverCardHeading,
  HoverCardTitle,
  HoverCardTitleSection,
  HoverCardTrigger,
} from "@src/lib/ui/HoverCard";
import MessageOutlineIcon from "@src/lib/icons/18px/message-outline";
import { Label } from "@src/lib/ui/Label";
import { cn } from "@src/lib/utils/cn";
import { RadioGroup, RadioGroupItem } from "@src/lib/ui/RadioGroup";
import Text from "@src/lib/ui/Text";
import { Textarea } from "@src/lib/ui/Textarea";
import { formatDateAbbreviated } from "@src/lib/utils/dates";
import React, { ReactElement, useState } from "react";
import { toast } from "sonner";
import type { ImpactReportingNewStoreOpeningsQuery } from "./ResultsWidgetsQuery.generated";
import { useUpdateStoreOpeningMutation } from "./ResultsWidgetsQuery.generated";

export type StoreOpening =
  ImpactReportingNewStoreOpeningsQuery["ImpactReportingNewStoreOpenings"][number];

export const EVALUATION_CONFIG: Record<
  StoreOpeningEvaluationStatus,
  {
    dot: string;
    bg: string;
    text: string;
    label: string;
    shortLabel: string;
    description: string;
  }
> = {
  [StoreOpeningEvaluationStatus.Good]: {
    dot: "bg-green-9",
    bg: "bg-green-1",
    text: "text-green-10",
    label: "Good opening",
    shortLabel: "Good",
    description: "Team was well-prepared and the opening went smoothly",
  },
  [StoreOpeningEvaluationStatus.Ok]: {
    dot: "bg-orange-9",
    bg: "bg-orange-1",
    text: "text-orange-10",
    label: "Ok opening",
    shortLabel: "Ok",
    description: "Some challenges but the team was able to manage",
  },
  [StoreOpeningEvaluationStatus.Poor]: {
    dot: "bg-red-9",
    bg: "bg-red-1",
    text: "text-red-10",
    label: "Poor opening",
    shortLabel: "Poor",
    description: "Significant delays or disruptions",
  },
};

export const formatOpeningDate = (dateStr: string): string =>
  formatDateAbbreviated(new Date(dateStr), {
    alwaysShowYear: true,
    showTime: false,
  });

export const EvaluationBadge = ({
  status,
  className,
}: {
  status: StoreOpeningEvaluationStatus;
  className?: string;
}): ReactElement => {
  const config = EVALUATION_CONFIG[status];
  return (
    <div
      className={cn(
        "flex shrink-0 items-center gap-1 rounded-lg px-2 py-1.5",
        config.bg,
        className,
      )}
    >
      <div className={cn("size-3 rounded-full", config.dot)} />
      <Text variant="fine-print-title" className={config.text}>
        {config.label}
      </Text>
    </div>
  );
};

export const EvaluationCell = ({
  opening,
  onEdit,
}: {
  opening: StoreOpening;
  onEdit: () => void;
}): React.ReactElement => {
  const status = opening.evaluationStatus;
  const [hoverOpen, setHoverOpen] = useState(false);
  return (
    <HoverCard openDelay={200} onOpenChange={setHoverOpen}>
      <HoverCardTrigger asChild>
        <div
          className={cn(
            "flex w-16 items-center gap-3 rounded-md px-1 py-3 transition-colors",
            hoverOpen && "bg-gray-1",
          )}
        >
          {status && (
            <div
              className={cn(
                "size-3 rounded-full",
                EVALUATION_CONFIG[status].dot,
              )}
            />
          )}
          {opening.evaluationNotes && (
            <MessageOutlineIcon
              className={cn(
                "size-3 transition-colors",
                hoverOpen ? "text-gray-10" : "text-gray-7",
              )}
            />
          )}
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="flex w-80 flex-col gap-4 p-5">
        <HoverCardHeading>
          <HoverCardTitleSection>
            <HoverCardTitle>{opening.locationName}</HoverCardTitle>
          </HoverCardTitleSection>
          {status && <EvaluationBadge status={status} />}
        </HoverCardHeading>
        {opening.evaluationNotes && (
          <div className="flex flex-col gap-2">
            <Text variant="compact-description" multiline>
              {opening.evaluationNotes}
            </Text>
            {opening.lastEditedByName && opening.lastEditedAt && (
              <Text variant="fine-print-description" className="text-gray-8">
                Last edited by {opening.lastEditedByName} on{" "}
                {formatOpeningDate(opening.lastEditedAt)}
              </Text>
            )}
          </div>
        )}
        <HoverCardButton onClick={onEdit}>
          {status ? "Edit evaluation" : "Add evaluation"}
        </HoverCardButton>
      </HoverCardContent>
    </HoverCard>
  );
};

export const DrawerDetailCard = ({
  opening,
  onEdit,
}: {
  opening: StoreOpening;
  onEdit: () => void;
}): ReactElement => (
  <div className="relative flex flex-col gap-5 rounded-xl border p-5">
    <div className="flex flex-col gap-0.5 pr-28">
      <Text variant="body-title" multiline>
        {opening.locationName}
      </Text>
      <Text variant="fine-print-description">
        Opened {formatOpeningDate(opening.openingDate!)}
      </Text>
    </div>
    {opening.evaluationStatus ? (
      <EvaluationBadge
        status={opening.evaluationStatus}
        className="absolute top-3 right-3"
      />
    ) : null}

    <div className="flex flex-col gap-3">
      {opening.evaluationNotes && (
        <div className="flex flex-col gap-1">
          <Text multiline variant="compact-description">
            {opening.evaluationNotes}
          </Text>
          {opening.lastEditedByName && opening.lastEditedAt && (
            <Text
              multiline
              variant="fine-print-description"
              className="text-gray-8"
            >
              Last edited by {opening.lastEditedByName} on{" "}
              {formatOpeningDate(opening.lastEditedAt)}
            </Text>
          )}
        </div>
      )}

      <Button variant="outline" size="sm" className="w-full" onClick={onEdit}>
        {opening.evaluationStatus ? "Edit evaluation" : "Add evaluation"}
      </Button>
    </div>
  </div>
);

export const DrawerEmptyCard = ({
  opening,
  onEdit,
}: {
  opening: StoreOpening;
  onEdit: () => void;
}): ReactElement => (
  <div className="rounded-xl border p-4">
    <div className="flex items-center justify-between">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Text variant="body-title" multiline>
          {opening.locationName}
        </Text>
        <Text variant="fine-print-description" className="text-gray-8">
          {opening.firstMemberAddedAt
            ? `Activated ${formatOpeningDate(opening.firstMemberAddedAt)}`
            : "No date set"}
        </Text>
      </div>
      <Button size="sm" className="shrink-0" onClick={onEdit}>
        Add opening details
      </Button>
    </div>
  </div>
);

export const AddOpeningDetailsDialog = ({
  opening,
  open,
  onOpenChange,
  isDemoMode = false,
}: {
  opening: StoreOpening;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isDemoMode?: boolean;
}): ReactElement => {
  const [openingDate, setOpeningDate] = useState<Date | undefined>(
    opening.openingDate ? new Date(opening.openingDate) : undefined,
  );
  const [evaluationStatus, setEvaluationStatus] = useState<string>(
    opening.evaluationStatus ?? "",
  );
  const [evaluationNotes, setEvaluationNotes] = useState(
    opening.evaluationNotes ?? "",
  );
  const [openingDateError, setOpeningDateError] = useState(false);
  const [updateStoreOpening, { loading: saving }] =
    useUpdateStoreOpeningMutation({
      refetchQueries: ["impactReportingNewStoreOpenings"],
    });

  const handleSave = async () => {
    if (isDemoMode) {
      toast.success("Demo mode: changes are not saved.");
      onOpenChange(false);
      return;
    }

    if (!openingDate) {
      setOpeningDateError(true);
      return;
    }

    await updateStoreOpening({
      variables: {
        input: {
          locationId: opening.locationId,
          openingDate: openingDate.toISOString(),
          evaluationStatus:
            (evaluationStatus as StoreOpeningEvaluationStatus) || null,
          evaluationNotes: evaluationNotes || null,
        },
      },
    });
    toast.success("Opening evaluation saved");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{opening.locationName}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1.5">
            <Label>Opening date</Label>
            <DatePicker
              date={openingDate}
              onSelect={(date) => {
                setOpeningDate(date);
                if (date) setOpeningDateError(false);
              }}
              placeholder="Pick a date"
              className={cn(
                "w-1/2",
                openingDateError && "border-red-9 focus-visible:ring-red-9",
              )}
            />
            {openingDateError && (
              <Text
                variant="fine-print-description"
                className="text-destructive"
              >
                Enter an opening date
              </Text>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>How would you rate the opening?</Label>
            <RadioGroup
              value={evaluationStatus}
              onValueChange={setEvaluationStatus}
              className="gap-2"
            >
              {(
                Object.entries(EVALUATION_CONFIG) as [
                  StoreOpeningEvaluationStatus,
                  (typeof EVALUATION_CONFIG)[StoreOpeningEvaluationStatus],
                ][]
              ).map(([value, config]) => (
                <label
                  key={value}
                  htmlFor={`evaluation-${value}`}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 shadow-sm transition-colors",
                    evaluationStatus === value
                      ? "border-primary"
                      : "border-input",
                  )}
                >
                  <RadioGroupItem value={value} id={`evaluation-${value}`} />
                  <div
                    className={cn("size-3 shrink-0 rounded-full", config.dot)}
                  />
                  <div className="flex flex-col">
                    <Text variant="compact-description">
                      {config.shortLabel}
                    </Text>
                    <Text
                      variant="compact-description"
                      className="text-muted-foreground"
                    >
                      {config.description}
                    </Text>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="evaluation-notes">
              What went well and what could be improved in future openings?
            </Label>
            <Textarea
              id="evaluation-notes"
              placeholder="Add notes about this opening..."
              value={evaluationNotes}
              onChange={(e) => setEvaluationNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter className="flex items-center sm:justify-between">
          {opening.lastEditedByName && opening.lastEditedAt ? (
            <div className="text-xs text-muted-foreground">
              <p>Last edited by {opening.lastEditedByName}</p>
              <p>{formatOpeningDate(opening.lastEditedAt)}</p>
            </div>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
